/**
 * DTemplate Conclusion Slide
 * The pre-seed's bet, built from the ground up: what we have, the three
 * loops it turns, and the race they make us a contender in
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { CurrencyDollar, FlagCheckered, MagicWand, UsersThree, type Icon } from '@phosphor-icons/react';
import { StatementSlide, useArrive, useStill } from '@components/press/pitch-deck/slides/dtemplate/frame';
import { colors } from '@config/colors';
import { typography } from '@config/typography';

/*
 * June, 2026-10-14: "the positioning of this is what we have: a product, a
 * way to improve that product, a household viral product, a CAC (coming
 * soon), a way to lower CAC, initial product-market love, broad market
 * interest, a strategy to move fast in 2027, and aiming for a valuable part
 * of the market that is differeniated from general AI. The bet with the
 * preseed is that the loops of improving the product, lowering the CAC, and
 * improving the household virality, will lead to being a contender in the
 * 2027 race for market share of AI Assistants for Households" — a
 * conclusion slide showing those points visually, building towards market
 * dominance.
 *
 * So it builds from the ground up, in her order: her signals and strategy
 * the ground; on it the three loops, each with what we have to turn it
 * (the CAC dashed: coming soon); over them, the race. Moving, the loops
 * turn together; still (the export) they stand.
 */

type Have = { what: string; how: string; soon?: boolean };

type LoopCard = {
  key: string;
  name: string;
  title: string;
  icon: Icon;
  have: readonly Have[];
};

const LOOPS: readonly LoopCard[] = [
  {
    key: 'product',
    name: 'The product loop',
    title: 'Improving the product',
    icon: MagicWand,
    have: [
      { what: 'A product', how: 'live on iOS and Android, with four products' },
      { what: 'A way to improve it', how: 'every wish makes it better for the next mom' },
    ],
  },
  {
    key: 'cac',
    name: 'The CAC loop',
    title: 'Lowering the CAC',
    icon: CurrencyDollar,
    have: [
      { what: 'A CAC, coming soon', how: 'measured by channel in the first test', soon: true },
      { what: 'A way to lower it', how: 'a bandit across channels, then word of mouth' },
    ],
  },
  {
    key: 'virality',
    name: 'The virality loop',
    title: 'Improving household virality',
    icon: UsersThree,
    have: [
      {
        what: 'A household-viral product',
        how: 'each mom brings her partner, sitters, parents and kids’ friends’ moms',
      },
    ],
  },
];

const GROUND: ReadonlyArray<{ label: string; have: readonly string[] }> = [
  { label: 'Early signals', have: ['Initial product-market love', 'Broad market interest'] },
  {
    label: 'Strategy',
    have: ['A strategy to move fast in 2027', 'Aimed at a valuable corner, differentiated from general AI'],
  },
];

/** The ring's size, and its track's radius. */
const RING = 104;
const RING_R = 42;

export const DConclusionSlide: React.FC = () => (
  <StatementSlide
    section="Conclusion"
    question="What’s the bet, and where does it lead?"
    headline="The pre-seed bet: three loops, turning together, make us a contender in 2027’s race to be households’ AI assistant."
  >
    <Build />
  </StatementSlide>
);

/** The race over the loops over the ground, arriving from the ground up. */
const Build: React.FC = () => {
  const arrive = useArrive();
  const still = useStill();
  return (
    <Stage>
      <Goal {...arrive(9)}>
        <Flag>
          <FlagCheckered size={34} weight="fill" color={colors.white} aria-hidden="true" />
        </Flag>
        <div>
          <GoalLabel>2027 · The race for market share of AI assistants for households</GoalLabel>
          <GoalLine>
            A contender, building towards <em>market dominance</em>
          </GoalLine>
        </div>
      </Goal>

      <Loops>
        {LOOPS.map((loop, i) => (
          <Column key={loop.key}>
            <Lift {...arrive(6 + i, { opacity: 0, y: 10 })} aria-hidden="true">
              <svg width="24" height="44" viewBox="0 0 24 44">
                <path d="M 12 44 V 14" stroke={colors.brand.exxtraDark} strokeWidth="4" strokeLinecap="round" />
                <path d="M 2 16 L 12 0 L 22 16 Z" fill={colors.brand.exxtraDark} />
              </svg>
            </Lift>
            <Card {...arrive(3 + i)}>
              <CardHead>
                <Ring icon={loop.icon} still={still} />
                <div>
                  <LoopName>{loop.name}</LoopName>
                  <LoopTitle>{loop.title}</LoopTitle>
                </div>
              </CardHead>
              <HaveList>
                {loop.have.map((have) => (
                  <li key={have.what}>
                    <Mark soon={have.soon} />
                    <span>
                      <strong>{have.what}</strong>
                      {have.how}
                    </span>
                  </li>
                ))}
              </HaveList>
            </Card>
          </Column>
        ))}
      </Loops>

      <Ground {...arrive(1)}>
        {GROUND.map((group) => (
          <GroundGroup key={group.label}>
            <GroundLabel>{group.label}</GroundLabel>
            <GroundList>
              {group.have.map((what) => (
                <li key={what}>
                  <Mark />
                  <strong>{what}</strong>
                </li>
              ))}
            </GroundList>
          </GroundGroup>
        ))}
      </Ground>
    </Stage>
  );
};

/** A loop: its track and arrowheads turning round its icon while the slide
 *  moves; still, standing. */
const Ring: React.FC<{ icon: Icon; still: boolean }> = ({ icon: Glyph, still }) => (
  <RingBox>
    <svg width={RING} height={RING} viewBox={`0 0 ${RING} ${RING}`} aria-hidden="true">
      <Turning $still={still}>
        <circle cx={RING / 2} cy={RING / 2} r={RING_R} fill="none" stroke={colors.brand.extraDark} strokeWidth="5" strokeDasharray="12 8" />
        {[0, 120, 240].map((deg) => (
          <path
            key={deg}
            d="M -8 -9 L 8 0 L -8 9 Z"
            fill={colors.accent.coral.extraDark}
            transform={`rotate(${deg} ${RING / 2} ${RING / 2}) translate(${RING / 2 + RING_R} ${RING / 2}) rotate(90)`}
          />
        ))}
      </Turning>
    </svg>
    <RingIcon>
      <Glyph size={34} weight="duotone" color={colors.brand.exxxtraDark} aria-hidden="true" />
    </RingIcon>
  </RingBox>
);

/** We have it: a seafoam disc and its tick. Coming soon: a dashed coral
 *  ring round a clock's hands. */
const Mark: React.FC<{ soon?: boolean }> = ({ soon = false }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" role="img" aria-label={soon ? 'Coming soon' : 'We have it'}>
    {soon ? (
      <>
        <circle cx="14" cy="14" r="12" fill={colors.white} stroke={colors.accent.coral.extraDark} strokeWidth="2.5" strokeDasharray="4 3" />
        <path d="M14 8.5V14l3.5 2.5" fill="none" stroke={colors.accent.coral.exxxtraDark} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ) : (
      <>
        <circle cx="14" cy="14" r="13" fill={colors.brand.exxtraDark} />
        <path d="M8.5 14.5l3.8 3.8 7.4-8" fill="none" stroke={colors.white} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
      </>
    )}
  </svg>
);

// ── Styles ───────────────────────────────────────────────────────────────────

const turn = keyframes`
  to { transform: rotate(360deg); }
`;

const Stage = styled.div`
  display: flex;
  flex-direction: column;
`;

/* The race: the deep seafoam the deck's panels wear, the flag in coral. */
const Goal = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  height: 112px;
  border-radius: 28px;
  background: ${colors.brand.exxxxtraDark};
  box-shadow: 0 18px 38px ${colors.shadowHard};
`;

const Flag = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.accent.coral.extraDark};
`;

const GoalLabel = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.brand.extraDark};
`;

const GoalLine = styled.div`
  margin-top: 4px;
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: 38px;
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: ${colors.white};

  em {
    font-style: normal;
    color: ${colors.accent.coral.light};
  }
`;

const Loops = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 40px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* Each loop, lifting into the race. */
const Lift = styled(motion.div)`
  height: 46px;
  display: flex;
  align-items: flex-start;
`;

const Card = styled(motion.div)`
  flex: 1;
  align-self: stretch;
  padding: 22px 26px;
  border-radius: 24px;
  background: ${colors.white};
  box-shadow: 0 14px 28px ${colors.shadowSoft};
  border-top: 5px solid ${colors.brand.extraDark};
`;

/* The names level across the cards, whether a title takes one line or two. */
const CardHead = styled.div`
  display: grid;
  grid-template-columns: ${RING}px 1fr;
  align-items: start;
  column-gap: 18px;

  > div:last-child {
    padding-top: 24px;
  }
`;

const RingBox = styled.div`
  position: relative;
  width: ${RING}px;
  height: ${RING}px;
`;

/* Round the ring's own centre, whatever the arrowheads' extent. */
const Turning = styled.g<{ $still: boolean }>`
  transform-box: view-box;
  transform-origin: ${RING / 2}px ${RING / 2}px;
  animation: ${(p) => (p.$still ? 'none' : turn)} 9s linear infinite;
`;

const RingIcon = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 60px;
  height: 60px;
  margin: -30px 0 0 -30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.brand.primary};
`;

const LoopName = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
`;

const LoopTitle = styled.div`
  margin-top: 4px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 26px;
  font-weight: 600;
  line-height: 1.2;
  color: ${colors.neutral.extraDark};
`;

const HaveList = styled.ul`
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    display: grid;
    grid-template-columns: 28px 1fr;
    column-gap: 12px;
    align-items: start;
  }

  span {
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 17px;
    line-height: 1.4;
    color: ${colors.neutral.darkMedium};
  }

  strong {
    display: block;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
    color: ${colors.neutral.extraDark};
  }
`;

/* The ground the loops stand on: the seafoam tint, under the white cards. */
/* The signals as wide as their words; the strategy, the longer, the rest. */
const Ground = styled(motion.div)`
  margin-top: 22px;
  display: grid;
  grid-template-columns: auto 1fr;
  padding: 18px 30px;
  border-radius: 22px;
  background: ${colors.brand.primaryLight};
  border: 2px solid ${colors.brand.primaryDark};
`;

const GroundGroup = styled.div`
  & + & {
    margin-left: 30px;
    padding-left: 30px;
    border-left: 2px solid ${colors.brand.primaryDark};
  }
`;

const GroundLabel = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
`;

/* Each point on one line but the last, which wraps in what's left. */
const GroundList = styled.ul`
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  column-gap: 40px;

  li {
    display: grid;
    grid-template-columns: 28px auto;
    column-gap: 12px;
    align-items: start;
    flex: 0 0 auto;
    white-space: nowrap;
  }

  li:last-child {
    flex: 1 1 0;
    white-space: normal;
    text-wrap: balance;
  }

  strong {
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
    color: ${colors.neutral.extraDark};
  }
`;
